import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TextField, TextAreaField, SelectField, ChipGroup } from "@/components/Form";
import { PHARMACY_TYPES, SOFTWARE } from "@/constants/options";
import { OnboardingExit } from "@/components/OnboardingExit";
import { US_STATES } from "@/constants/states";
import { PhiWarning } from "@/components/Notices";

const empty = {
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "NY",
  zip_code: "",
  pharmacy_type: "Independent",
  software: [],
  description: "",
  hide_name: false,
  npi: "",
};

export default function PharmacyProfileForm({ onboarding = false }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [npiResult, setNpiResult] = useState(null);
  const [npiBusy, setNpiBusy] = useState(false);

  const verifyNpi = async () => {
    setNpiBusy(true);
    setNpiResult(null);
    try {
      const { data } = await api.post("/pharmacy/npi/verify", { npi: form.npi });
      setNpiResult({ ok: data.verified, message: data.message });
      await loadPharmacy();
    } catch (e) {
      setNpiResult({ ok: false, message: apiError(e) });
    } finally {
      setNpiBusy(false);
    }
  };

  const loadPharmacy = useCallback(async () => {
    const { data } = await api.get("/pharmacy/profile");
    if (data) setForm({ ...empty, ...data });
    else
      setForm((f) => ({
        ...f,
        contact_person: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
      }));
  }, [user]);

  useEffect(() => {
    loadPharmacy();
  }, [loadPharmacy]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api.put("/pharmacy/profile", form);
      await refresh();
      setSaved(true);
      if (onboarding) navigate("/pharmacy/post");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      {onboarding && <OnboardingExit backTo="/pharmacy/npi" backLabel="Back to NPI step" />}
      <p className="overline">{onboarding ? "Pharmacy setup" : "Pharmacy profile"}</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
        {onboarding ? "Set up your pharmacy" : "Pharmacy profile"}
      </h1>
      <p className="mt-3 text-sm text-slate-600">Only the pharmacy name and ZIP are required — you can fill the rest later.</p>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="pharmacy-profile-form">
        {form.npi_verified ? (
          <section className="card-surface space-y-4 p-6" data-testid="pharmacy-registry-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-semibold">From the NPI Registry</h2>
              <span className="pill border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="npi-verified-badge">
                NPI verified
              </span>
            </div>
            <dl className="grid gap-4 sm:grid-cols-2">
              {[
                ["Pharmacy name (DBA)", form.name],
                ["Legal name", form.npi_legal_name],
                ["NPI", form.npi],
                ["Address", form.address],
                ["City", form.city],
                ["State", form.state],
                ["ZIP code", form.zip_code],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="overline">{label}</dt>
                  <dd className="mt-1 text-sm font-medium text-slate-800">{value || "—"}</dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-slate-500">
              These details come from the official NPPES NPI Registry and cannot be edited here. Update
              them at nppes.cms.hhs.gov and re-verify.
            </p>
          </section>
        ) : (
          <>
            <section className="card-surface space-y-5 p-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField label="Pharmacy name" required value={form.name} onChange={(e) => set("name", e.target.value)} testId="pharmacy-name" />
              </div>
            </section>
            <section className="card-surface space-y-5 p-6">
              <h2 className="font-display text-xl font-semibold">Location</h2>
              <TextField label="Address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} testId="pharmacy-address" />
              <div className="grid gap-5 sm:grid-cols-3">
                <TextField label="City / neighborhood" value={form.city || ""} onChange={(e) => set("city", e.target.value)} placeholder="Ridgewood, Queens" testId="pharmacy-city" />
                <SelectField label="State" options={US_STATES} value={form.state || "NY"} onChange={(e) => set("state", e.target.value)} testId="pharmacy-state" />
                <TextField label="ZIP code" required value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="11385" testId="pharmacy-zip" />
              </div>
            </section>
          </>
        )}

        <section className="card-surface space-y-5 p-6">
          <h2 className="font-display text-xl font-semibold">Contact details</h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Contact person" value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} testId="pharmacy-contact" />
            <TextField label="Email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} testId="pharmacy-email" />
            <TextField label="Phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} testId="pharmacy-phone" />
          </div>
        </section>

        <section className="card-surface space-y-5 p-6">
          <h2 className="font-display text-xl font-semibold">Pharmacy details</h2>
          <SelectField label="Pharmacy type" options={PHARMACY_TYPES} value={form.pharmacy_type} onChange={(e) => set("pharmacy_type", e.target.value)} testId="pharmacy-type" />
          <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 accent-[#1E3A8A]"
              checked={!!form.hide_name}
              onChange={(e) => set("hide_name", e.target.checked)}
              data-testid="pharmacy-hide-name"
            />
            <span>
              <strong>Hide mode</strong> — do not show my pharmacy name to workers. Your shifts will display
              only the city, ZIP code and the shift requirements. Your name, address and contact details are
              revealed once a shift is confirmed.
            </span>
          </label>
          <ChipGroup label="Pharmacy software" options={SOFTWARE} value={form.software} onChange={(v) => set("software", v)} testIdPrefix="pharmacy-software" />
          <TextAreaField label="Description" value={form.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="Easy-going store, good tech support, parking available." testId="pharmacy-description" />
          <PhiWarning testId="pharmacy-description-phi-warning" />
        </section>

        {!form.npi_verified && (
        <section className="card-surface space-y-4 p-6" data-testid="npi-section">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-semibold">NPI verification</h2>
            {form.npi_verified ? (
              <span className="pill border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="npi-verified-badge">
                NPI verified
              </span>
            ) : (
              <span className="pill border-slate-200 bg-slate-50 text-slate-500" data-testid="npi-unverified-badge">
                Not verified
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600">
            We check your 10-digit NPI against the public NPPES NPI Registry and confirm it is active and
            registered in your pharmacy's state or ZIP code. Verified pharmacies show a badge to workers.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[12rem]">
              <TextField
                label="NPI number"
                value={form.npi || ""}
                onChange={(e) => set("npi", e.target.value)}
                placeholder="1234567893"
                testId="pharmacy-npi"
              />
            </div>
            <button
              type="button"
              onClick={verifyNpi}
              disabled={npiBusy || !form.npi}
              className="btn-primary"
              data-testid="verify-npi-button"
            >
              {npiBusy ? "Checking registry…" : "Verify NPI"}
            </button>
          </div>
          {form.npi_registry_name && (
            <p className="text-sm text-slate-600" data-testid="npi-registry-name">
              Registry record: {form.npi_registry_name}
              {form.npi_city ? ` · ${form.npi_city}, ${form.npi_state}` : ""}
            </p>
          )}
          {npiResult && (
            <p
              className={`rounded-lg px-3 py-2 text-sm ${npiResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-900"}`}
              data-testid="npi-result"
            >
              {npiResult.message}
            </p>
          )}
        </section>
        )}

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="pharmacy-profile-error">{error}</p>}
        {saved && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="pharmacy-profile-saved">Pharmacy saved.</p>}

        <button type="submit" disabled={busy} className="btn-primary" data-testid="pharmacy-profile-submit">
          {busy ? "Saving…" : onboarding ? "Save & post first shift" : "Save pharmacy"}
        </button>
      </form>
    </div>
  );
}
